<template>
  <div class="helper-text">
    <span v-if="allocation">{{ allocation }}</span>
    <span v-if="allocation && destClosestParent" class="in">in</span>
    <component :is="icon" class="icon" v-bind="iconProps" />
    <span v-if="helper">{{ helper }}</span>
  </div>
</template>

<script lang="ts">
import { formatDuration, isSameYear } from "date-fns";
import * as _ from "lodash-es";
import { match, Pattern } from "ts-pattern";
import { computed, defineComponent, PropType, toRefs } from "vue";

import controller from "@ctrl/controller";
import type {
  GraphDatabase,
  MandateDates,
  MandateDestination,
  Page,
  ReferenceId,
  RoutineMandateCompletion,
} from "@ctrl/controller-rpc";

import { formatDate } from "@/components/dates";
import EventIcon from "@/components/icons/EventIcon.vue";
import PageIcon from "@/components/icons/PageIcon.vue";
import PersonIcon from "@/components/icons/PersonIcon.vue";
import ReferenceIcon from "@/components/icons/ReferenceIcon.vue";
import TaskIcon from "@/components/icons/TaskIcon.vue";
import * as inject from "@/components/inject";
import { getWorkspaceContextLabel } from "@/components/reference-context";
import { RouteNames } from "@/routes/types";
import { computeHierarchy, parentObject } from "@/utils/hierarchy";

import { Recurrence } from "./recurrence";

const { nextOccurrences } = controller;

export default defineComponent({
  props: {
    completion: {
      type: Object as PropType<RoutineMandateCompletion>,
      required: true,
    },
  },
  async setup(props) {
    const { completion } = toRefs(props);
    const locale = inject.locale();
    const controller = inject.controller();
    const profile = inject.profile();

    const destClosestParent =
      completion.value.value.type === "destination" &&
      completion.value.value.value.type === "document"
        ? await match(completion.value.value.value)
            .with({ id: { kind: "task" } }, async (obj) => {
              const task = await controller.task.get(obj.id.id);
              task.release();
              if (task.value?.parent)
                return await parentObject(task.value, controller, profile);
            })
            .with({ id: { kind: "task-rec" } }, async (obj) => {
              const recurrentTask = await controller.taskRecurrent.get(
                obj.id.id
              );
              recurrentTask.release();
              if (recurrentTask.value?.parent)
                return await parentObject(
                  recurrentTask.value,
                  controller,
                  profile
                );
            })
            .with({ id: { kind: "page" } }, async (obj) => {
              const page = await controller.page.get(obj.id.id);
              page.release();
              // No native-`parent` pre-check for pages: their parent lives
              // on the extension row, which `parentObject` reads itself.
              if (page.value)
                return await parentObject(page.value, controller, profile);
            })
            //To uncomment when Event or People have parents
            /*          .with({ id: { kind: "event" } }, (obj) => {
          const event: Event | undefined = controller.event.get(
            obj.id.id
          ).value;
          if (
            event?.parent
          )
            return parentObject(event, controller);
        })
        .with({ id: { kind: "people" } }, (obj) => {
          const person: People | undefined = controller.people.get(
            obj.id.id
          ).value;
          if (
            person?.parent
          )
            return parentObject(person, controller);
        })*/
            .otherwise(() => null)
        : null;

    const displayHierarchy = async (page: Page | undefined) => {
      if (page) {
        const breadcrumbItems = await computeHierarchy(
          page,
          controller,
          profile
        );
        if (breadcrumbItems.value.length == 1) {
          return `${breadcrumbItems.value[0].title}`;
        } else if (breadcrumbItems.value.length == 2) {
          return `${breadcrumbItems.value[0].title} / ${breadcrumbItems.value[1].title}`;
        } else if (breadcrumbItems.value.length > 2) {
          return `${breadcrumbItems.value[0].title} / … / ${
            breadcrumbItems.value[breadcrumbItems.value.length - 1].title
          }`;
        }
      }
    };

    // Compute the string to display when the completion is of type destination
    const getDestinationHelper = async (mandate: MandateDestination) => {
      return match(mandate)
        .with({ type: "calendar" }, async (m) => {
          const calendar = await controller.calendar.get(m.id);
          calendar.release();
          return calendar.value ? `${calendar.value.connection}` : undefined;
        })
        .with(
          { type: "document", id: { kind: "event" } },
          async ({ id: { id } }) => {
            const event = await controller.event.get(id);
            event.release();
            if (event.value) {
              const calendar = await controller.calendar.get(
                event.value.calendar
              );
              calendar.release();
              return calendar.value
                ? calendar.value.summary
                : event.value.title;
            }
          }
        )
        .with(
          { type: "document", id: { kind: "event-rec" } },
          async ({ id: { id } }) => {
            const event = await controller.event.recurrent.get(id);
            event.release();
            if (event.value) {
              const calendar = await controller.calendar.get(
                event.value.calendar
              );
              calendar.release();
              return calendar.value
                ? calendar.value.summary
                : event.value.title;
            }
          }
        )
        .with(
          { type: "document", id: { kind: "page" } },
          async ({ id: { id }, section }) => {
            if (section) {
              //FIXME: When a hierarchy is available for sections in a Document (H1, H2, H3) display that hierarchy there.
              return section[0];
            } else {
              const page = await controller.page.get(id);
              page.release();
              if (page.value) return await displayHierarchy(page.value);
            }
          }
        )
        .with(
          { type: "document", id: { kind: "people" } },
          async ({ id: { id } }) => {
            const people = await controller.people.get(id);
            people.release();
            if (people.value) {
              return people.value.email;
            } else return null;
          }
        )
        .with({ type: "document", id: { kind: "task" } }, async () => {
          if (destClosestParent?.type === "page") {
            const hierarchy = await displayHierarchy(destClosestParent.obj);
            return hierarchy
              ? `${hierarchy} / ${destClosestParent?.title}`
              : destClosestParent?.title;
          } else {
            return destClosestParent?.title;
          }
        })
        .with(
          { type: "document", id: { kind: "task-rec" } },
          async ({ id: { id } }) => {
            const task = await controller.taskRecurrent.get(id);
            task.release();
            if (task.value) {
              return task.value.title;
            } else `recurrent task ${id}`;
          }
        )
        .with(
          { type: "document", id: { kind: "object" } },
          async ({ id }: { id: ReferenceId }) => {
            const db = await controller.graph.database.ofObject(id.id);
            db.release();
            return db.value?.data_name || `object ${id.id}`;
          }
        )
        .exhaustive();
    };

    // Compute the string to display when the completion is of type date
    const getDateHelper = (mandate: MandateDates) => {
      return match(mandate)
        .with({ type: "date" }, (m) => formatDate(m.date))
        .with({ type: "date_range" }, (mandate) => {
          return `${formatDate(mandate.start)} → ${formatDate(mandate.end)}`;
        })
        .with(
          { type: "month" },
          (m) =>
            `${_.capitalize(m.month)} ${
              isSameYear(m.year, new Date().getFullYear()) ? "" : m.year
            }`
        )
        .with({ type: "year" }, ({ year }) => year.toString())
        .with({ type: "week" }, ({ week }) => {
          return locale.value.formatWeek(week);
        })
        .exhaustive();
    };

    // Get organization and workspace name for a database type completion
    const getTypeHelper = async (database: GraphDatabase) => {
      return (
        getWorkspaceContextLabel(profile.value, database.workspace) ||
        database.name
      );
    };

    const helper = await match(completion.value.value)
      .with({ type: "daytime" }, ({ value }) => {
        return locale.value.formatDaytime(value);
      })
      .with({ type: "duration" }, (comp) => {
        return formatDuration({
          hours: Math.floor(comp.value / 3600),
          minutes: (comp.value % 3600) / 60,
        });
      })
      .with({ type: "dates" }, (comp) => getDateHelper(comp.value))
      .with(
        { type: "destination" },
        async (comp) => await getDestinationHelper(comp.value)
      )
      .with({ type: "recurrence" }, (comp) => {
        const occurrences = nextOccurrences(1, comp.value, null);
        return formatDate(occurrences[0]);
      })
      .with({ type: "participant" }, (comp) => {
        return match(comp.value)
          .with(Pattern.string, (email) => email)
          .with({ helper: Pattern.select() }, (helper) => helper)
          .exhaustive();
      })
      .with({ type: "type" }, async ({ value }) => await getTypeHelper(value))
      .with({ type: "label" }, () => null)
      .with({ type: "priority" }, () => null)
      .exhaustive();

    const icon = computed(() => {
      if (destClosestParent)
        return (
          match(destClosestParent)
            .with({ type: RouteNames.Event }, () => EventIcon)
            .with({ type: RouteNames.Person }, () => PersonIcon)
            .with({ type: "page" }, () => PageIcon)
            .with({ type: "task" }, () => TaskIcon)
            // ReferenceIcon resolves the custom object's type icon from its id.
            .with({ type: "object" }, () => ReferenceIcon)
            .otherwise(() => null)
        );
      else return null;
    });

    const iconProps = computed(() => {
      if (destClosestParent) {
        return match(destClosestParent)
          .with({ type: RouteNames.Event }, ({ obj }) => {
            return {
              recurring:
                "recurrence" in obj
                  ? Recurrence.Recurring
                  : Recurrence.NotRecurring,
            };
          })
          .with({ type: "task" }, ({ obj }) => {
            return {
              recurring:
                "recurrence" in obj
                  ? Recurrence.Recurring
                  : Recurrence.NotRecurring,
            };
          })
          .with({ type: "object" }, ({ id }) => ({
            target: { kind: "object", id },
          }))
          .with(Pattern._, () => null)
          .exhaustive();
      } else return null;
    });

    const allocation =
      completion.value.value.type === "destination"
        ? await match(completion.value.value.value)
            .with({ type: "document", id: { kind: "task" } }, async (doc) => {
              const task = await controller.task.get(doc.id.id);
              task.release();
              if (
                task.value &&
                task.value.allocations &&
                task.value.allocations.length > 0
              ) {
                const id = task.value.allocations[0];
                const event = await controller.event.get(id);
                event.release();
                if (event.value) return formatDate(event.value.start);
              }
            })
            .otherwise(() => null)
        : null;

    //If we need to display the time of the parent of the destination
    /*    const destParentTime = computed(() => {
      if (destClosestParent)
        return match(destClosestParent)
          .with({ type: RouteNames.Event }, ({ obj: event }) =>
            formatDate(event.start)
          )
          .with({ type: "task" }, ({ obj: task }) => {
            if (
              task &&
              "allocations" in task &&
              task.allocations &&
              task.allocations.length > 0
            ) {
              const event = controller.event.get(task.allocations[0]).value;
              if (event) return formatDate(event.start);
            }
          })
          .otherwise(() => null);
      else return null;
    });*/

    return {
      destClosestParent,
      iconProps,
      allocation,
      helper,
      icon,
    };
  },
});
</script>
<style scoped lang="scss">
.helper-text {
  display: flex;
  align-items: center;
  flex-direction: row;
  column-gap: 6px;

  .in {
    @include themed() {
      color: t($grey-30);
    }
  }

  .icon {
    display: flex;
    flex-shrink: 0;

    :deep(svg) {
      height: 16px;
      width: 16px;
    }

    :deep(path.stroke) {
      @include themed() {
        stroke: t($grey-50);
      }
    }

    :deep(path.fill) {
      @include themed() {
        fill: t($grey-50);
      }
    }

    :deep(.recurring) {
      &.badge-bg {
        height: 13px;
        width: 13px;

        svg {
          height: 10px;
          width: 10px;
        }
      }
    }
  }

  &.selected .icon :deep(.recurring.badge-bg) {
    @include themed() {
      background: t($grey-10);
    }
  }
}
</style>
